import { useContext } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Button } from "@stackje/shared-components/ui/button";
import { AuthContext } from "@/context/AuthContext";
import { User, LogOut, CreditCard, Bug, LayoutTemplate } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";

/** Offers the owner's Builder site, or an opt-in path that keeps their current platform address. */
export const UserAccountPopover = ({ builderSiteId = null, canStartBuilder = false }: { builderSiteId?: string | null; canStartBuilder?: boolean }) => {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  if (!authContext?.user) {
    return null;
  }

  const { user, logout } = authContext;

  const handleLogout = async () => {
    const { error } = await logout();
    if (!error) {
      navigate({ to: "/auth/login", search: { returnTo: undefined } });
    }
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button variant="ghost" size="icon">
          <User className="h-5 w-5" />
        </Button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="min-w-56 bg-white rounded-md p-1 shadow-lg border border-gray-200 z-50"
          sideOffset={5}
          align="end"
        >
          <DropdownMenu.Item
            disabled
            className="flex items-center px-3 py-2 text-sm text-gray-700 cursor-default"
          >
            <div className="flex flex-col">
              <span className="text-xs text-gray-500 mb-1">Signed in as</span>
              <span className="font-medium">{user.email}</span>
            </div>
          </DropdownMenu.Item>
          
          <DropdownMenu.Separator className="h-px bg-gray-200 my-1" />

          {builderSiteId && (
            <>
              <DropdownMenu.Item
                className="flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer rounded-sm outline-none focus:bg-gray-50"
                onClick={() => navigate({ to: "/builder" })}
              >
                <LayoutTemplate className="h-4 w-4 mr-3" />
                Open your new site builder
              </DropdownMenu.Item>

              <DropdownMenu.Separator className="h-px bg-gray-200 my-1" />
            </>
          )}
          {!builderSiteId && canStartBuilder && (
            <>
              <DropdownMenu.Item
                className="flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer rounded-sm outline-none focus:bg-gray-50"
                onClick={() => navigate({ to: "/start" })}
              >
                <LayoutTemplate className="h-4 w-4 mr-3" />
                Build with the new site builder
              </DropdownMenu.Item>

              <DropdownMenu.Separator className="h-px bg-gray-200 my-1" />
            </>
          )}
          
          <DropdownMenu.Item
            className="flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer rounded-sm outline-none focus:bg-gray-50"
            onClick={() => navigate({ to: "/subscription" })}
          >
            <CreditCard className="h-4 w-4 mr-3" />
            Manage Subscription
          </DropdownMenu.Item>
          
          <DropdownMenu.Separator className="h-px bg-gray-200 my-1" />
          
          <DropdownMenu.Item
            className="flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer rounded-sm outline-none focus:bg-gray-50"
            onClick={() => window.open("https://clear-otter-ab4.notion.site/2665de9ab7fc809ea3abc984a58786fa?pvs=105", "_blank")}
          >
            <Bug className="h-4 w-4 mr-3" />
            Report an Issue
          </DropdownMenu.Item>
          
          <DropdownMenu.Separator className="h-px bg-gray-200 my-1" />
          
          <DropdownMenu.Item
            className="flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer rounded-sm outline-none focus:bg-gray-50"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 mr-3" />
            Logout
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
